import { Column } from '@tanstack/react-table';

import { Doc } from '@/api/types/backend-dto-types';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { ProcessingState } from '@/new/components/processing-chip';
import { Label } from '@/new/components/shadcn/label';

interface DocumentsExplorerFilterPopoverProps {
  clearAllFilters: () => void;
  isProcessedColumn?: Column<Doc, unknown>;
  createdDateColumn?: Column<Doc, unknown>;
}

export function DocumentsExplorerFilterPopover({
  clearAllFilters,
  isProcessedColumn,
  createdDateColumn,
}: DocumentsExplorerFilterPopoverProps) {
  const isProcessedFilterValue = isProcessedColumn?.getFilterValue() as ProcessingState[] | ProcessingState | undefined;
  const processingValues = Array.isArray(isProcessedFilterValue)
    ? isProcessedFilterValue
    : isProcessedFilterValue
      ? [isProcessedFilterValue]
      : [];

  // Extract date range from column filter
  const dateFilter = createdDateColumn?.getFilterValue() as { start: Date | null; end: Date | null } | undefined;
  const uploadStartDate = dateFilter?.start ?? null;
  const uploadEndDate = dateFilter?.end ?? null;

  const setUploadStartDate = (date: Date | null) => {
    const currentFilter = (createdDateColumn?.getFilterValue() as { start: Date | null; end: Date | null }) || {
      start: null,
      end: null,
    };
    createdDateColumn?.setFilterValue({ ...currentFilter, start: date });
  };

  const setUploadEndDate = (date: Date | null) => {
    const currentFilter = (createdDateColumn?.getFilterValue() as { start: Date | null; end: Date | null }) || {
      start: null,
      end: null,
    };
    createdDateColumn?.setFilterValue({ ...currentFilter, end: date });
  };

  const processingOptions = [ProcessingState.Processed, ProcessingState.Processing, ProcessingState.NotProcessed];

  const formatProcessingLabel = (value: ProcessingState) => {
    switch (value) {
      case ProcessingState.Processed:
        return 'Processed';
      case ProcessingState.Processing:
        return 'Processing';
      case ProcessingState.NotProcessed:
        return 'Not Processed';
      default:
        return value;
    }
  };

  return (
    <FilterPopoverContent clearAllFilters={clearAllFilters}>
      {isProcessedColumn && (
        <div>
          <Label className="mb-2 block">Processing Status</Label>
          <FilterMultiSelect
            values={processingValues}
            options={processingOptions}
            column={isProcessedColumn}
            placeholder="Select processing status..."
            formatLabel={formatProcessingLabel}
          />
        </div>
      )}

      <div>
        <Label className="mb-2 block">Upload Date Range</Label>
        <DateRangeFilter
          startDate={uploadStartDate}
          endDate={uploadEndDate}
          setStartDate={setUploadStartDate}
          setEndDate={setUploadEndDate}
        />
      </div>
    </FilterPopoverContent>
  );
}
